import Cookies from 'js-cookie'
import {
  getCurrentEmailChangeRedirectFromLocation,
  rememberPendingEmailChangeRedirect,
} from '@/utils/emailChangeRedirect'

// 🔧 新增: localStorage版本控制
const STORAGE_VERSION = '2.0'  // 当版本号变化时，会清除旧数据
const STORAGE_VERSION_KEY = 'storage_version'

// 各种存储键名
const TokenKey = 'access_token'
const UserInfoKey = 'user_info'
const TokenSourceKey = 'token_source'

// 🔧 新增: 检查并更新localStorage版本
function checkStorageVersion() {
  try {
    const currentVersion = localStorage.getItem(STORAGE_VERSION_KEY)
    
    if (currentVersion !== STORAGE_VERSION) {
      console.log(`🔄 检测到localStorage版本变化: ${currentVersion} -> ${STORAGE_VERSION}，清除旧数据`)
      
      // 清除所有用户相关数据
      const userRelatedKeys = [
        'user_info', 'user', 'user_id', 'current_user',
        'current_tenant', 'current_tenant_id', 'available_tenants'
      ]
      
      userRelatedKeys.forEach(key => {
        if (localStorage.getItem(key)) {
          console.log(`  清除旧数据: ${key}`)
          localStorage.removeItem(key)
        }
      })
      
      // 更新版本号
      localStorage.setItem(STORAGE_VERSION_KEY, STORAGE_VERSION)
      console.log('✅ localStorage版本更新完成')
    }
  } catch (error) {
    console.error('检查localStorage版本失败:', error)
  }
}

// 初始化时检查版本
checkStorageVersion()

function isUsableJwtToken(candidate) {
  if (typeof candidate !== 'string') return false
  const value = candidate.trim()
  const parts = value.split('.')
  return value && !['undefined', 'null', 'none'].includes(value.toLowerCase()) && parts.length === 3 && parts.every(Boolean)
}

// 获取token
export function getToken() {
  // 只把后端可解析的 JWT 作为认证令牌。旧的 `token` 键可能残留一段
  // token（或测试/跳转值），不能因为它排在前面而遮蔽有效 cookie/备份。
  const localToken = localStorage.getItem(TokenKey)
  const cookieToken = Cookies.get(TokenKey)
  const backupToken = localStorage.getItem('token_backup')
  const legacyToken = localStorage.getItem('token')
  const candidates = [localToken, cookieToken, backupToken, legacyToken]
  const token = candidates.find(isUsableJwtToken) || null
  
  // 如果所有来源都没有token，尝试重新获取和同步
  if (!token) {
    console.log('getToken: token不存在')
    
    // 检查URL中是否有token参数（处理页面刷新情况）
    try {
      const urlParams = new URLSearchParams(window.location.search || window.location.hash.split('?')[1] || '');
      const urlToken = urlParams.get('access_token') || urlParams.get('token');
      
      if (isUsableJwtToken(urlToken)) {
        console.log('从URL参数中恢复token')
        setToken(urlToken)
        return urlToken
      }
    } catch (e) {
      console.error('检查URL参数失败:', e)
    }
    
    // 如果前面没有找到token，但应用内部状态中有token，尝试恢复
    try {
      const stores = window.document.__pinia || {}
      const userStore = Object.values(stores).find(store => 
        isUsableJwtToken(store.token)
      )
      
      if (userStore && userStore.token) {
        console.log('从Pinia存储中恢复token:', userStore.token.substring(0, 10) + '...')
        // 重新保存到localStorage和cookie
        setToken(userStore.token)
        return userStore.token
      }
    } catch (error) {
      console.error('尝试从Pinia恢复token失败:', error)
    }
    
    return null
  }
  
  // 同步token到所有存储位置，确保一致性
  if (token) {
    // 确保选中的有效 token 同步到各存储位置，清除旧值造成的漂移。
    if (localToken !== token) {
      localStorage.setItem(TokenKey, token)
    }
    if (cookieToken !== token) {
      Cookies.set(TokenKey, token)
    }
    if (backupToken !== token) {
      localStorage.setItem('token_backup', token)
    }
  }
  
  return token
}

// 设置token
export function setToken(token) {
  if (!token) {
    console.error('setToken: 尝试设置空token')
    return null
  }
  
  try {
    // 多重保存token，确保不会丢失
    localStorage.setItem(TokenKey, token)
    Cookies.set(TokenKey, token)
    localStorage.setItem('token_set_time', new Date().toISOString())
    
    // 备份token，以防localStorage或cookie被清除
    localStorage.setItem('token_backup', token)
    
    // 验证token是否正确保存
    const savedToken = localStorage.getItem(TokenKey)
    if (!savedToken) {
      console.error('Token保存验证失败，尝试再次保存')
      // 再次尝试保存
      setTimeout(() => {
        localStorage.setItem(TokenKey, token)
        Cookies.set(TokenKey, token)
      }, 100)
    }
    
    return token
  } catch (error) {
    console.error('设置token失败:', error)
    return null
  }
}

// 记录token来源
export function setTokenSource(source) {
  localStorage.setItem(TokenSourceKey, source || 'unknown')
  localStorage.setItem('login_time', new Date().toISOString())
  console.log(`Token来源已记录: ${source}`)
}

// 获取token来源
export function getTokenSource() {
  return localStorage.getItem(TokenSourceKey)
}

// 移除token
export function removeToken() {
  console.log('removeToken移除token')
  try {
    localStorage.removeItem(TokenKey)
    localStorage.removeItem('token_backup')
    Cookies.remove(TokenKey)
    localStorage.removeItem('token_set_time')
    localStorage.removeItem(TokenSourceKey)
  } catch (error) {
    console.error('移除token失败:', error)
  }
}

// 设置用户信息
export function setUserInfo(userInfo) {
  if (!userInfo) {
    console.error('setUserInfo: 尝试设置空用户信息')
    return null
  }
  
  console.log('setUserInfo:', userInfo ? JSON.stringify(userInfo).substring(0, 50) + '...' : 'null')
  
  try {
    if (typeof userInfo === 'object') {
      localStorage.setItem(UserInfoKey, JSON.stringify(userInfo))
      // 备份
      localStorage.setItem('user', JSON.stringify(userInfo))
    } else if (typeof userInfo === 'string') {
      try {
        // 尝试解析字符串为JSON
        const userObj = JSON.parse(userInfo)
        localStorage.setItem(UserInfoKey, JSON.stringify(userObj))
        localStorage.setItem('user', JSON.stringify(userObj))
      } catch (e) {
        console.error('设置用户信息失败，无法解析JSON:', e)
        localStorage.setItem(UserInfoKey, userInfo)
      }
    }
    
    // 记录用户信息设置时间
    localStorage.setItem('user_info_set_time', new Date().toISOString())
    console.log('用户信息已保存')
  } catch (error) {
    console.error('设置用户信息失败:', error)
  }
}

// 获取用户信息
export function getUserInfo() {
  // 尝试从多个位置获取用户信息
  const userInfo = localStorage.getItem(UserInfoKey) || localStorage.getItem('user');
  if (!userInfo) {
    console.log('getUserInfo: 用户信息不存在')
    return null
  }
  
  try {
    return JSON.parse(userInfo)
  } catch (e) {
    console.error('解析用户信息失败:', e)
    return userInfo
  }
}

// 移除用户信息
export function removeUserInfo() {
  console.log('removeUserInfo移除用户信息')
  try {
    localStorage.removeItem(UserInfoKey)
    localStorage.removeItem('user')
    localStorage.removeItem('user_info_set_time')
  } catch (error) {
    console.error('移除用户信息失败:', error)
  }
}

// 清除所有认证信息
export function clearAuth() {
  console.log('clearAuth清除所有认证信息');
  removeToken()
  removeUserInfo()
  
  // ✅ 清除会员信息（本地 + Pinia，避免切换账号后沿用上一用户档位）
  try {
    localStorage.removeItem('user_membership')
    console.log('✅ 会员信息已清除')
  } catch (error) {
    console.error('❌ 清除会员信息失败:', error)
  }
  void import('@/stores/membership')
    .then(({ useMembershipStore }) => {
      try {
        useMembershipStore().clearMembership()
      } catch (e) {
        console.warn('⚠️ 清除会员 Pinia 状态失败:', e)
      }
    })
    .catch(() => {})
  
  // 触发多窗口同步事件 - 通知其他窗口用户已退出登录
  try {
    const logoutTimestamp = Date.now().toString()
    localStorage.setItem('logout_timestamp', logoutTimestamp)
    
    // 短暂延迟后移除退出登录标识，避免影响正常使用
    setTimeout(() => {
      localStorage.removeItem('logout_timestamp')
    }, 1000)
    
    console.log('已触发退出登录事件，通知其他窗口同步')
  } catch (error) {
    console.error('触发退出登录事件失败:', error)
  }
}

// 验证认证状态是否有效
export function isAuthValid() {
  const token = getToken();
  
  // 添加token有效性检查 - 检查token是否为有效格式
  if (!token) {
    console.log('isAuthValid: token不存在');
    return false;
  }
  
  // 检查token格式是否有效（简单检查是否为JWT格式）
  const tokenParts = token.split('.');
  if (tokenParts.length !== 3) {
    console.log('isAuthValid: token格式无效');
    return false;
  }
  
  // 检查token是否过期（解析JWT的payload部分）
  try {
    const payload = JSON.parse(atob(tokenParts[1]));
    if (payload.exp) {
      const expiryTime = payload.exp * 1000; // 转换为毫秒
      const currentTime = Date.now();
      
      // 如果token已过期，清除认证信息并返回false
      if (currentTime > expiryTime) {
        console.log(`isAuthValid: token已过期，过期时间: ${new Date(expiryTime).toISOString()}`);
        clearAuth(); // 清除过期的认证信息
        return false;
      }
      
      // 记录token剩余有效期
      const remainingTime = (expiryTime - currentTime) / (1000 * 60 * 60); // 小时
      console.log(`isAuthValid: token有效，剩余有效期: ${remainingTime.toFixed(2)}小时`);
      
      // 如果token即将过期（小于24小时），记录警告日志
      if (remainingTime < 24) {
        console.warn(`isAuthValid: token即将过期，剩余: ${remainingTime.toFixed(2)}小时`);
      }
    }
  } catch (error) {
    console.error('isAuthValid: 解析token失败', error);
    // 解析失败不一定意味着token无效，可能是格式问题，继续执行
  }
  
  // 检查token是否存在于所有存储位置
  const localToken = localStorage.getItem(TokenKey);
  const cookieToken = Cookies.get(TokenKey);
  const backupToken = localStorage.getItem('token_backup');
  
  // 如果token在不同存储位置不一致，进行同步
  if (localToken !== cookieToken || localToken !== backupToken) {
    console.warn('isAuthValid: token在不同存储位置不一致，进行同步');
    setToken(token); // 重新保存token，确保一致性
  }
  
  console.log(`isAuthValid检查结果: true (token有效)`);
  return true;
}

// 从URL解析并保存token和用户信息 (优化函数)
export function parseUrlTokenAndSave() {
  console.log('开始从URL解析token和用户信息')
  
  // 检查当前是否在重置密码页面，如果是则跳过token处理
  const currentPath = window.location.hash.replace('#', '') || window.location.pathname
  if (currentPath.includes('/reset-password') || currentPath.includes('/forgot-password')) {
    console.log('在重置密码/忘记密码页面，跳过URL token解析')
    return false
  }
  
  try {
    const urlParams = new URLSearchParams(window.location.search || window.location.hash.split('?')[1] || '');
    const accessToken = urlParams.get('access_token');
    const tokenType = urlParams.get('token_type');
    const tenantCode = urlParams.get('tenant');
    const userInfoStr = urlParams.get('userInfo'); // 用户信息参数
    const legacyToken = urlParams.get('token'); // 兼容旧的token参数
    const emailChangeAction = urlParams.get('emailChangeAction');
    const activationType = urlParams.get('type');
    const errorParam = urlParams.get('error'); // 检查是否有错误参数
    const errorMessage = urlParams.get('message'); // 错误信息

    if (currentPath.includes('/activate')) {
      console.log('检测到激活链接，跳过认证token处理')
      return false
    }

    if (emailChangeAction === 'confirm-current') {
      const emailChangeRedirect = getCurrentEmailChangeRedirectFromLocation()
      if (emailChangeRedirect) {
        rememberPendingEmailChangeRedirect(emailChangeRedirect)
      }
      console.log('检测到邮箱更改确认链接，跳过认证token处理')
      return false
    }

    // 检查是否有错误参数
    if (errorParam) {
      console.error(`飞书登录返回错误: ${errorParam}, 信息: ${errorMessage || '未提供错误详情'}`);
      return false;
    }

    const finalToken = accessToken || legacyToken;

    if (!finalToken) {
      console.log('URL中没有找到access_token或token参数');
      return false;
    }
    
    // 额外检查：如果token是重置密码token（包含邮箱），则跳过处理
    try {
      const tokenParts = finalToken.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1].replace(/-/g, '+').replace(/_/g, '/')));
        // 如果token的sub字段是邮箱地址，这是重置密码token，不应该作为认证token处理
        if (payload.sub && typeof payload.sub === 'string' && payload.sub.includes('@')) {
          console.log('检测到重置密码token，跳过认证token处理')
          return false
        }
      }
    } catch (e) {
      // 解析失败，继续处理
    }

    console.log(`从URL获取到token (类型: ${accessToken ? 'access_token' : 'token'}), 长度: ${finalToken.length}`);

    // 验证token格式
    if (finalToken.length < 20) {
      console.error('Token格式无效，长度过短');
      return false;
    }

    // 先清除旧的认证信息
    clearAuth();

    // 设置token - 多重保障，确保token被保存
    const savedToken = setToken(finalToken);
    // 额外备份token
    localStorage.setItem('token_backup', finalToken);
    Cookies.set(TokenKey, finalToken);
    
    // 验证token是否正确保存
    const verifyToken = getToken();
    if (!verifyToken) {
      console.error('验证token失败，token可能未正确保存，尝试再次保存');
      // 最后一次尝试保存token
      localStorage.setItem(TokenKey, finalToken);
      Cookies.set(TokenKey, finalToken);
      
      // 延迟检查token是否已保存
      setTimeout(() => {
        const delayedToken = getToken();
        if (!delayedToken) {
          console.error('延迟验证token仍然失败');
        } else {
          console.log('延迟验证token成功');
        }
      }, 500);
    }

    // 设置token来源
    setTokenSource(accessToken ? 'feishu_callback_access_token' : 'url_params_token');
    
    // 记录登录时间
    localStorage.setItem('login_time', new Date().toISOString());

    // 尝试解析token (JWT格式验证)
    try {
      // 简单验证JWT格式 (header.payload.signature)
      const tokenParts = finalToken.split('.');
      if (tokenParts.length === 3) {
        // 解析payload部分
        const payload = JSON.parse(atob(tokenParts[1].replace(/-/g, '+').replace(/_/g, '/')));
        console.log('Token payload解析成功，包含字段:', Object.keys(payload).join(', '));
        
        // 检查过期时间
        if (payload.exp) {
          const expiryTime = payload.exp * 1000; // 转换为毫秒
          const currentTime = Date.now();
          const remainingTime = (expiryTime - currentTime) / (1000 * 60 * 60); // 小时
          
          if (currentTime > expiryTime) {
            console.error('Token已过期，过期时间:', new Date(expiryTime).toISOString());
            // 不立即返回false，继续处理，让后续请求拦截器处理过期问题
          } else {
            console.log(`Token有效，剩余有效期: ${remainingTime.toFixed(2)}小时`);
          }
          
          // 存储过期时间，便于后续检查
          localStorage.setItem('token_expiry_time', expiryTime.toString());
        }
        
        // 存储租户ID（如果存在）
        if (payload.tenant_id) {
          localStorage.setItem('current_tenant_id', payload.tenant_id.toString());
          console.log(`从Token中提取租户ID: ${payload.tenant_id}`);
        }
        
        // 存储用户角色（如果存在）
        if (payload.role) {
          localStorage.setItem('user_role', payload.role);
          console.log(`从Token中提取用户角色: ${payload.role}`);
        }
        
        // 存储用户ID（如果存在）
        if (payload.user_id) {
          localStorage.setItem('user_id', payload.user_id.toString());
          console.log(`从Token中提取用户ID: ${payload.user_id}`);
        }
      }
    } catch (parseError) {
      console.warn('解析Token失败，将继续处理:', parseError);
      // 解析失败不阻止流程，可能是非JWT格式的token
    }

    // 存储token类型和租户信息 (如果存在)
    if (tokenType) {
      localStorage.setItem('token_type', tokenType);
      console.log(`保存token类型: ${tokenType}`);
    }
    
    // 如果有用户信息参数，解析并保存
    if (userInfoStr) {
      try {
        const decodedStr = decodeURIComponent(userInfoStr);
        console.log('解码后的用户信息字符串:', decodedStr.substring(0, 50) + '...');
        
        // 尝试解析为JSON对象
        try {
          const userInfo = JSON.parse(decodedStr);
          console.log('成功解析用户信息JSON:', Object.keys(userInfo).join(', '));
          
          // 存储用户信息
          setUserInfo(userInfo);
          
          // 单独保存一些关键字段，提高可靠性
          if (userInfo.id) localStorage.setItem('user_id', userInfo.id.toString());
          if (userInfo.role) localStorage.setItem('user_role', userInfo.role);
          if (userInfo.tenant_id) localStorage.setItem('current_tenant_id', userInfo.tenant_id.toString());
          
        } catch (jsonError) {
          console.warn('用户信息不是有效的JSON格式，将作为字符串保存:', jsonError);
          setUserInfo(decodedStr);
        }
      } catch (e) {
        console.error('解析URL中的用户信息失败:', e);
        // Token已保存，用户信息解析失败不应阻止流程，但需要记录
      }
    }

    // 清理URL中的参数，避免残留
    if (window.history.replaceState) {
        // 尝试移除哈希后的查询参数
        const cleanHash = window.location.hash.split('?')[0];
        const cleanUrl = window.location.pathname + window.location.search.split('?')[0] + cleanHash;
        window.history.replaceState({}, document.title, cleanUrl);
        console.log('已清理URL中的认证参数');
    } else {
        console.warn('无法清理URL参数，浏览器不支持history.replaceState');
    }

    console.log('成功从URL解析并保存了认证信息');
    return true;
  } catch (error) {
    console.error('解析URL中的token和用户信息失败:', error);
    return false;
  }
}

// 专门用于重置密码页面的认证清理函数
export function clearAuthForResetPassword() {
  console.log('重置密码页面：开始清除所有认证数据')
  
  // 清除localStorage中的所有认证相关数据
  const authKeys = [
    'access_token', 'token', 'token_backup', 
    'user', 'user_info', 'user_id', 'user_role',
    'current_tenant_id', 'token_type', 'token_source',
    'login_time', 'token_set_time', 'user_info_set_time',
    'token_expiry_time', 'login_source', 'auth_monitor_redirect'
  ]
  
  authKeys.forEach(key => {
    if (localStorage.getItem(key)) {
      console.log(`重置密码页面：清除localStorage: ${key}`)
      localStorage.removeItem(key)
    }
  })
  
  // 清除cookies中的认证信息
  const cookieKeys = ['access_token', 'token']
  cookieKeys.forEach(key => {
    try {
      Cookies.remove(key)
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      console.log(`重置密码页面：清除cookie: ${key}`)
    } catch (e) {
      console.warn(`清除cookie ${key} 失败:`, e)
    }
  })
  
  // 清除sessionStorage中可能的认证信息
  const sessionKeys = ['access_token', 'token', 'user']
  sessionKeys.forEach(key => {
    if (sessionStorage.getItem(key)) {
      console.log(`重置密码页面：清除sessionStorage: ${key}`)
      sessionStorage.removeItem(key)
    }
  })
  
  // 设置一个标记，表示当前处于重置密码状态
  localStorage.setItem('reset_password_mode', 'true')
  localStorage.setItem('reset_password_time', new Date().toISOString())
  
  console.log('重置密码页面：认证数据清除完成')
}

// 检查是否处于重置密码模式
export function isInResetPasswordMode() {
  const resetMode = localStorage.getItem('reset_password_mode')
  const resetTime = localStorage.getItem('reset_password_time')
  
  if (!resetMode || !resetTime) {
    return false
  }
  
  // 检查重置密码模式是否过期（10分钟）
  const resetTimestamp = new Date(resetTime).getTime()
  const now = Date.now()
  const tenMinutes = 10 * 60 * 1000
  
  if (now - resetTimestamp > tenMinutes) {
    localStorage.removeItem('reset_password_mode')
    localStorage.removeItem('reset_password_time')
    return false
  }
  
  return true
}

// 清除重置密码模式
export function clearResetPasswordMode() {
  localStorage.removeItem('reset_password_mode')
  localStorage.removeItem('reset_password_time')
  console.log('重置密码模式已清除')
}
